import { type ReactElement, type ReactNode, useEffect, useState } from "react";

import { DropdownButton } from "@vega-core/components/Buttons/DropdownButton/DropdownButton";
import { type ThemedButtonProps } from "@vega-core/components/Buttons/ThemedButton";
import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { Button } from "@vega-core/components/shadcn/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@vega-core/components/shadcn/select";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

export type ThemedDropdownOptionType = {
  disabled?: boolean;
  label: string;
  value: string;
};

export type ThemedDropdownProps = {
  options?: ThemedDropdownOptionType[];
  customAriaLabel?: string;
  clearLabel?: string;
  customLabelForUnavailable?: string;
  emptyListPlaceholder?: ReactElement;
  error?: string;
  hint?: string;
  isClearable?: boolean;
  label?: string | null;
  labelClassName?: string;
  labelTag?: ReactNode;
  onChange?: (value: ThemedDropdownOptionType | undefined) => void;
  placeholder?: string | null;
  renderValue?: (value: string) => ReactNode;
  value?: string;
  isLoading?: boolean;
  fullWidth?: boolean;
  buttonProps?: Partial<ThemedButtonProps>;
  asButton?: boolean;
  className?: string;
  renderOption?: (option: ThemedDropdownOptionType) => ReactNode;
  renderPlaceholder?: (placeholder: string) => ReactNode;
  name: string;
  disabled?: boolean;
  variant?: "compact" | "default" | "small";
};

export function ThemedDropdown({
  customAriaLabel,
  clearLabel,
  customLabelForUnavailable,
  emptyListPlaceholder,
  error,
  hint,
  isClearable = true,
  label,
  labelClassName,
  labelTag,
  onChange,
  options = [],
  placeholder,
  renderValue,
  value,
  isLoading,
  fullWidth = false,
  asButton = false,
  buttonProps,
  className,
  renderOption,
  renderPlaceholder,
  disabled,
  name,
  variant = "default",
}: ThemedDropdownProps) {
  const [key, setKey] = useState(+new Date());
  const { isOpen, setIsOpen, toggle } = useBoolean(false);

  const [selectedValue, setSelectedValue] = useState<string | undefined>(value);

  useEffect(handleInitialValue, [value]);

  function handleInitialValue() {
    setSelectedValue(value);
  }

  const handleChange = (newValue: string) => {
    const selectedOption = options.find(
      (option) => option.value === newValue,
    ) as ThemedDropdownOptionType;
    setSelectedValue(newValue);
    onChange?.(selectedOption);
  };

  const availableOptions = options.filter(
    (option) => !(option.disabled ?? false),
  );
  const unavailableOptions = options.filter((option) => option.disabled);

  // Helper function to render an option
  const renderOptionItem = (option: ThemedDropdownOptionType) => {
    if (renderOption) {
      return renderOption(option);
    }

    return option.label;
  };

  const isCompact = variant === "compact";
  const isSmall = variant === "small";

  const placeholderText = placeholder ?? m.core_select_an_option();

  const selectedOption = options.find((opt) => opt.value === selectedValue);
  const selectedOptionLabel = selectedOption?.label || selectedValue || "";

  const getDisplayText = () => {
    if (!selectedValue) return placeholderText;

    if (renderValue) return renderValue(selectedOptionLabel);

    return selectedOptionLabel;
  };

  const displayTextForButton = getDisplayText();

  return (
    <div className={cnTwMerge({ "w-full": fullWidth }, className)}>
      {isTruthy(label) && (
        <FormLabel
          compact={isSmall}
          hint={hint}
          label={label}
          labelClassName={labelClassName}
          tag={labelTag}
        />
      )}
      <Select
        key={key}
        onOpenChange={setIsOpen}
        onValueChange={handleChange}
        value={selectedValue}
      >
        {asButton ? (
          <SelectTrigger isOpen={isOpen} noStyle>
            <DropdownButton
              aria-label={customAriaLabel ?? label ?? ""}
              dataTestId={`${name}-dropdown`}
              disabled={availableOptions.length === 0 || disabled}
              fullWidth={fullWidth}
              isLoading={isLoading}
              onClick={toggle}
              open={isOpen}
              {...buttonProps}
            >
              {displayTextForButton}
            </DropdownButton>
          </SelectTrigger>
        ) : (
          <SelectTrigger
            aria-label={customAriaLabel ?? label ?? ""}
            className={cnTwMerge(
              "w-full body-base-regular",
              {
                "skeleton-box": isLoading,
                "min-h-7.5": isSmall,
              },
              buttonProps?.className,
            )}
            data-testid={`${name}-dropdown`}
            disabled={availableOptions.length === 0 || disabled}
            isOpen={isOpen}
            key={value}
            noStyle={isCompact}
          >
            {renderValue && selectedValue ? (
              renderValue(selectedOptionLabel)
            ) : (
              <SelectValue
                placeholder={
                  renderPlaceholder?.(placeholderText) ?? placeholderText
                }
              />
            )}
          </SelectTrigger>
        )}
        <SelectContent className="overflow-auto">
          {isClearable ? (
            <Button
              className={cnTwMerge(
                "w-full items-left justify-start px-4",
                !selectedValue && "bg-background/component",
              )}
              data-testid={`${name}-clear-button`}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedValue(undefined);
                onChange?.(undefined);
                setKey(+new Date());
              }}
              size="sm"
              variant="ghost"
            >
              {clearLabel ?? m.common_all()}
            </Button>
          ) : null}
          <SelectGroup>
            {availableOptions.length > 0 ? (
              availableOptions.map((option) => (
                <SelectItem
                  data-testid={`${name}-dropdown-item-${option.value}`}
                  key={option.value}
                  value={option.value}
                >
                  {renderOptionItem(option)}
                </SelectItem>
              ))
            ) : (
              <div>{emptyListPlaceholder || m.core_no_options_available()}</div>
            )}
          </SelectGroup>
          {unavailableOptions.length > 0 && (
            <SelectGroup>
              <SelectLabel>
                {customLabelForUnavailable ?? m.core_unavailable_options()}
              </SelectLabel>
              {unavailableOptions.map((option) => (
                <SelectItem
                  data-testid={`${name}-dropdown-item-${option.value}`}
                  disabled
                  key={option.value}
                  value={option.value}
                >
                  {renderOptionItem(option)}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>
      {isTruthy(error) && (
        <ValidationErrorText dataTestId={error} withMargin>
          {error}
        </ValidationErrorText>
      )}
    </div>
  );
}
